import React, { useState, useEffect } from 'react';
import { Input, Select, Button, FormGroup, Textarea } from '../components/Form';
import { getAgendadores, submitDiarioCloser } from '../services/api';
import { logError } from '../lib/logError';
import { hojeSP } from '../lib/tz';

// Diário Closer reformulado (2026-07-14): com a nova estrutura GHL, a realização/comparecimento das
// reuniões é marcada no pipeline do GHL — não mais neste diário. Aqui o closer registra apenas as
// indicações coletadas no dia + aprendizados/desafios, mantendo um relatório diário para o cargo.
function DiarioCloser() {
    const [closers, setClosers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');


    const initialFormState = {
        data: hojeSP(),
        nome: '',
        indicacoes_coletadas: '',
        aprendizados: '',
        desafios: '',
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        const fetchClosers = async () => {
            setIsLoading(true);
            try {
                const data = await getAgendadores();
                const filteredClosers = data.filter(member =>
                    member.cargo && (
                        member.cargo.trim().toLowerCase() === "closer" ||
                        member.cargo.trim().toLowerCase() === "sales farmer"
                    )
                );
                setClosers(filteredClosers);
            } catch (error) {
                logError(error, { page: 'DiarioCloser', phase: 'initial-fetch' });
                setErrorMessage('Não foi possível carregar a lista de Closers.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchClosers();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        if (value === '' || /^\d+$/.test(value)) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const payloadToSubmit = {
                ...formData,
                indicacoes_coletadas: parseInt(formData.indicacoes_coletadas) || 0,
                aprendizados: formData.aprendizados || '',
                desafios: formData.desafios || '',
            };

            const response = await submitDiarioCloser(payloadToSubmit);
            if (!response.success) {
                setErrorMessage(response.error || 'Ocorreu um erro ao enviar o diário.');
                return;
            }

            setSuccessMessage('Diário Closer registrado com sucesso!');
            setFormData({ ...initialFormState, data: hojeSP() });
            window.scrollTo(0, 0);
        } catch (error) {
            setErrorMessage(error?.message || 'Erro ao enviar o diário.');
            window.scrollTo(0, 0);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Diário Closer</h1>
                    <p className="page-subtitle">Registre as indicações coletadas e suas observações do dia.</p>
                </div>
            </div>

            <div className="page-body">
                {successMessage && (
                    <div className="alert alert-success">{successMessage}</div>
                )}
                {errorMessage && (
                    <div className="alert alert-error">{errorMessage}</div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <p className="section-label">Informações Gerais</p>
                        <div className="grid-2">
                            <FormGroup label="Data" htmlFor="data">
                                <Input
                                    type="date"
                                    id="data"
                                    name="data"
                                    value={formData.data}
                                    onChange={handleChange}
                                    required
                                />
                            </FormGroup>

                            <FormGroup label="Nome do Closer" htmlFor="nome">
                                <Select
                                    id="nome"
                                    name="nome"
                                    value={formData.nome}
                                    onChange={handleChange}
                                    required
                                    isLoading={isLoading}
                                >
                                    <option value="">Selecione o Closer</option>
                                    {closers.map(closer => (
                                        <option key={closer.nome} value={closer.nome}>{closer.nome}</option>
                                    ))}
                                </Select>
                            </FormGroup>
                        </div>
                    </div>

                    <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <p className="section-label">Métricas</p>
                        <FormGroup label="Indicações Coletadas" htmlFor="indicacoes_coletadas">
                            <Input
                                type="number"
                                min="0"
                                id="indicacoes_coletadas"
                                name="indicacoes_coletadas"
                                value={formData.indicacoes_coletadas}
                                onChange={handleNumberChange}
                                placeholder="0"
                                required
                            />
                        </FormGroup>
                    </div>

                    <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <p className="section-label">Fechamento</p>
                        <FormGroup label="Aprendizados do Dia" htmlFor="aprendizados">
                            <Textarea
                                id="aprendizados"
                                name="aprendizados"
                                value={formData.aprendizados}
                                onChange={handleChange}
                                placeholder="Descreva os principais aprendizados ou observações do dia..."
                                rows="4"
                                style={{ resize: 'vertical' }}
                                required
                            />
                        </FormGroup>

                        <FormGroup label="Desafios do Dia" htmlFor="desafios">
                            <Textarea
                                id="desafios"
                                name="desafios"
                                value={formData.desafios}
                                onChange={handleChange}
                                placeholder="Quais os principais desafios enfrentados hoje?"
                                rows="4"
                                style={{ resize: 'vertical' }}
                                required
                            />
                        </FormGroup>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.25rem' }}>
                        <Button type="submit" isLoading={isSubmitting}>
                            Registrar Diário Closer
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
}

export default DiarioCloser;
